import { CreateTeamDialog } from "./-components/create-team-dialog"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { type ColumnDef } from "@tanstack/react-table"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Badge } from "@/components/ui/badge"
import { DataTable } from "@/components/ui/data-table"
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header"
import { getCountryFlag } from "@/lib/country-utils"

export const Route = createFileRoute("/(admin)/admin/teams/")({
  component: RouteComponent,
})

interface TeamWithDetails {
  _id: Id<"teams">
  _creationTime: number
  name: string
  type: "teams" | "pairs"
  captainId?: Id<"users">
  memberCount: number
  captain: { _id: Id<"users">; name: string } | null
  country: string | null
}

function RouteComponent() {
  const navigate = useNavigate()
  const teams = useQuery(api.teams.handlers.listAllTeamsForAdmin)

  // Define columns with navigation
  const columns: ColumnDef<TeamWithDetails>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Team Name" />
      ),
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    },
    {
      accessorKey: "type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      cell: ({ row }) => (
        <Badge variant="outline" className="capitalize">
          {row.original.type}
        </Badge>
      ),
    },
    {
      id: "captain",
      header: "Captain",
      cell: ({ row }) => row.original.captain?.name ?? "No captain",
    },
    {
      accessorKey: "memberCount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Members" />
      ),
      cell: ({ row }) => (
        <span className="text-sm">{row.original.memberCount}</span>
      ),
    },
    {
      accessorKey: "country",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Country" />
      ),
      cell: ({ row }) => {
        const country = row.original.country
        if (!country) {
          return <span className="text-muted-foreground">—</span>
        }
        const flag = getCountryFlag(country)
        return (
          <span className="flex items-center gap-1.5">
            <span>{flag}</span>
            <span>{country}</span>
          </span>
        )
      },
    },
  ]

  if (teams === undefined) {
    return <div className="p-4">Loading...</div>
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Teams</h1>
          <p className="text-sm text-muted-foreground">
            Manage teams, members, and scheduled quizzes
          </p>
        </div>
        <CreateTeamDialog />
      </div>

      <DataTable
        columns={columns}
        data={teams}
        filterColumn="name"
        filterPlaceholder="Search by team name..."
        onRowClick={(team) => {
          navigate({
            to: "/admin/teams/$teamId",
            params: { teamId: team._id },
          }).catch(console.error)
        }}
      />
    </div>
  )
}
